import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Button, Card, CheckboxField, DateField, Stack, Typography } from "@curves/ui-base";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import { getNowTime, getFirstOfMonth, isValidDate } from "@util/moment";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import { ConsumerSection, InvoiceSection, PaymentSection, ReceiverSection, TermSection, CartTable, SelectWithMenuItems } from "@icoach/healthyCyclePurchase/components";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { EmployeeSelect, FocusModeToggle } from "@icoach/components";
import { ConfirmTemporaryContractDialog } from "@icoach/documents/dialog";
import { useCyclePurchaseMemberSelection, useCyclePurchaseOrderSubmit, useCyclePurchaseTemporaryOrderSubmit, useHCProductData } from "@icoach/healthyCyclePurchase/hooks";
import useQueryManager from "@util/hook/useQueryManager";
import useOptions from "@util/hook/useOptions";
import { Grid } from "@mui/material";
import { getEffectiveDate, isPriceNoticePeriod } from "@icoach/healthyCyclePurchase/CyclePurchase.util";

const CyclePurchasePage = () => {
	const { searchParams } = useQueryManager();

	// 管理選項資料 Hook
	const { getCyclePurchaseOptionsApi } = useCyclePurchaseApi();
	const { options } = useOptions({
		getOptionsApi: getCyclePurchaseOptionsApi,
		isFetchOnMount: true,
	});

	// 管理商品資料 Hook
	const products = useHCProductData();

	// 使用 Hook 管理會員選擇和訂單資料
	const { cycleOrder, openDialog, handleMemberChange, handleDialogClose, handleDialogYes, handleDialogNo, memberID } = useCyclePurchaseMemberSelection({
		initialMemberID: searchParams?.memberID,
	});

	// 使用 Hooks 處理提交邏輯
	const { submit: submitOrder, isSubmitting: isSubmittingOrder, validationErrors, clearValidationErrors } = useCyclePurchaseOrderSubmit();
	const { submit: submitTemporaryOrder, isSubmitting: isSubmittingTemporary } = useCyclePurchaseTemporaryOrderSubmit();

	// refs
	const consumerRef = useRef(null);
	const productItemsRef = useRef(null);
	const productConfirmRef = useRef(null);
	const shippingDayRef = useRef(null);
	const firstShipmentMonthRef = useRef(null);
	const receiverRef = useRef(null);
	const paymentRef = useRef(null);
	const invoiceRef = useRef(null);
	const termRef = useRef(null);
	const cycleOrderDateRef = useRef(null);
	const timeRef = useRef(null);
	const toolRef = useRef(null);
	const clubRef = useRef(null);
	const saleClubRef = useRef(null);
	const employeeRef = useRef(null);

	// 聚焦模式 ref
	const focusModeRef = useRef(null);

	// 管理首次出貨月份的 minDate
	const [firstShippingMinDate, setFirstShippingMinDate] = useState(getFirstOfMonth(getNowTime()));

	// 處理出貨週期變更
	const handleShippingDayChange = useCallback(
		(e, n, val) => {
			if (val && options?.shippingDayOptions) {
				// 找到選擇的 shippingDay 選項
				const selectedOption = options.shippingDayOptions.find(item => item.value === val);
				if (selectedOption && selectedOption.text) {
					// 使用 text（實際日期，如 "10" 或 "20"）來計算 minDate
					const shippingDayText = parseInt(selectedOption.text, 10);
					const effectiveDate = getEffectiveDate(getNowTime(), shippingDayText);
					const minDate = getFirstOfMonth(effectiveDate);
					setFirstShippingMinDate(minDate);
				}
			}
		},
		// eslint-disable-next-line
		[options?.shippingDayOptions],
	);

	// 當 cycleOrder 有 shippingDay 時，初始化 minDate
	useEffect(
		() => {
			if (cycleOrder?.shippingDay && options?.shippingDayOptions) {
				const selectedOption = options.shippingDayOptions.find(item => item.value === cycleOrder.shippingDay);
				if (selectedOption && selectedOption.text) {
					const shippingDayText = parseInt(selectedOption.text, 10);
					const effectiveDate = getEffectiveDate(getNowTime(), shippingDayText);
					const minDate = getFirstOfMonth(effectiveDate);
					setFirstShippingMinDate(minDate);
				}
			}
		},
		// eslint-disable-next-line
		[cycleOrder?.shippingDay, options?.shippingDayOptions],
	);

	// 啟用無操作計時器
	useInactivityTimerToggle();

	const getParams = () => {
		let result = {
			cycleOrderType: 1, // 目前只有正常配送
			cycleOrderID: cycleOrder?.cycleOrderID ?? 0,
			cycleOrderNo: cycleOrder?.cycleOrderNo ?? "",
		};

		// 訂購人資訊
		if (consumerRef.current && consumerRef.current.getResult) {
			result = Object.assign(result, consumerRef.current.getResult());
		}

		// 訂購商品
		if (productItemsRef.current && productItemsRef.current.getResult) {
			result.items = productItemsRef.current.getResult();
		}

		// 出貨資料
		if (shippingDayRef.current && shippingDayRef.current.getResult) {
			result.shippingDay = shippingDayRef.current.getResult() || 0;
		}

		if (firstShipmentMonthRef.current && firstShipmentMonthRef.current.getResult) {
			result.firstShipmentMonth = firstShipmentMonthRef.current.getResult() || "";
		}

		// 收件人資料
		if (receiverRef.current && receiverRef.current.getResult) {
			Object.assign(result, receiverRef.current.getResult());
		}

		// 付款資料
		if (paymentRef.current && paymentRef.current.getResult) {
			Object.assign(result, paymentRef.current.getResult());
		}
		
		// 發票資料
		if (invoiceRef.current && invoiceRef.current.getResult) {
			Object.assign(result, invoiceRef.current.getResult());
		}

		// 訂購條款
		if (termRef.current && termRef.current.getResult) {
			result = Object.assign(result, termRef.current.getResult());
		}

		// 店舖教練資料
		if (cycleOrderDateRef.current?.getResult) {
			result.cycleOrderDate = cycleOrderDateRef.current.getResult();
		}

		if (timeRef.current && timeRef.current.getResult) {
			result.cycleTimeType = timeRef.current.getResult() || 0;
		}

		if (toolRef.current && toolRef.current.getResult) {
			result.cycleToolType = toolRef.current.getResult() || 0;
		}

		if (clubRef.current && clubRef.current.getResult) {
			result.clubID = clubRef.current.getResult() || 0;
		}

		if (saleClubRef.current && saleClubRef.current.getResult) {
			result.saleClubID = saleClubRef.current.getResult() || 0;
		}

		if (employeeRef.current && employeeRef.current.getResult) {
			result.employeeID = employeeRef.current.getResult() || 0;
		}
		return result;
	};

	const handleSubmit = () => {
		// 防止重複提交
		if (isSubmittingOrder() || isSubmittingTemporary()) return;

		// 清除舊有錯誤訊息
		clearValidationErrors();

		const isError = refIsRequiredError(
			consumerRef,
			productItemsRef,
			productConfirmRef,
			shippingDayRef,
			firstShipmentMonthRef,
			receiverRef,
			paymentRef,
			invoiceRef,
			termRef,
			cycleOrderDateRef,
			timeRef,
			toolRef,
			clubRef,
			saleClubRef,
			employeeRef,
		);
		if (!isError) {
			const params = getParams();
			submitOrder(params);
		}
	};

	const handleTemporaryCycleOrder = () => {
		if (isSubmittingTemporary()) return false;

		if (!refIsRequiredError(consumerRef)) {
			const params = getParams();
			submitTemporaryOrder(params, cycleOrder?.cycleOrderID ?? 0);
		}
	};

	return (
		<Box className="container main-container-spacing cycle-purchase-page">
			<Box component={"header"} className={"page-header"}>
				<Typography className={"page-header-title"} variant={"h3"}>
					健康產品循環訂購單
				</Typography>
			</Box>
			<FocusModeToggle ref={focusModeRef}>
				<Card className={"order-info-card mb-4"}>
					{/* 訂購人資訊 */}
					<ConsumerSection ref={consumerRef} initialSource={cycleOrder || {}} memberID={memberID} onMemberChange={handleMemberChange} errorModule={validationErrors} />

					{/* 訂購商品」 */}
					<Card.CardHeader title="訂購商品" className="bg-light-active px-4" />
					<Card.CardContent className="px-4 pt-2 pb-4">
						<CartTable ref={productItemsRef} key={cycleOrder?.items} sourceData={cycleOrder?.items ?? []} className="mb-4" resource={products} />
						<Stack direction="column" className="bg-info-light px-4 py-2 mb-4">
							<CheckboxField ref={productConfirmRef} required>
								<CheckboxField.Checkbox label="我同意於提出變更口味申請後，刷卡金額會依品項單價而不同。" value={true} required />
							</CheckboxField>
						</Stack>
					</Card.CardContent>

					{/* 出貨資訊 */}
					<Card.CardHeader title="出貨資訊" className="bg-light-active px-4" />
					<Card.CardContent className="p-4">
						<Grid spacing={2} container>
							<Grid xs={4} item>
								<SelectWithMenuItems
									label="出貨週期"
									ref={shippingDayRef}
									defaultValue={cycleOrder?.shippingDay > 0 ? cycleOrder.shippingDay : ""}
									options={options?.shippingDayOptions ?? []}
									onChange={handleShippingDayChange}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<DateField
									label="首次出貨月份"
									ref={firstShipmentMonthRef}
									key={`${cycleOrder?.firstShipmentMonth}-${firstShippingMinDate}`}
									defaultValue={isValidDate(cycleOrder?.firstShipmentMonth) ? cycleOrder?.firstShipmentMonth : firstShippingMinDate}
									views={["year", "month"]}
									openTo={"month"}
									format={"YYYY/MM"}
									mask={"____/__"}
									minDate={firstShippingMinDate}
									placeholder={"請選擇月份"}
									fullWidth
									required
								/>
							</Grid>
						</Grid>
					</Card.CardContent>

					{/* 收件資訊 */}
					<ReceiverSection ref={receiverRef} initialSource={cycleOrder || {}} memberID={memberID} errorModule={validationErrors} />

					{/* 付款資訊 */}
					<PaymentSection ref={paymentRef} memberID={memberID} initialSource={cycleOrder?.payment || {}} cycleOrder={cycleOrder} errorModule={validationErrors} />
					{/* 發票資訊 */}
					<InvoiceSection ref={invoiceRef} memberID={memberID} initialSource={cycleOrder?.invoice || {}} resource={options} errorModule={validationErrors} />
				</Card>
				{/* 訂購條款 */}
				<TermSection className="mb-4" ref={termRef} errorModule={validationErrors} showPriceNotice={isPriceNoticePeriod()} />
				{/* 店舖教練資訊 */}
				<Card className={"order-info-card mb-4"}>
					<Card.CardContent className={"p-4"}>
						<Grid spacing={2} container>
							<Grid xs={4} item>
								<DateField
									label="訂購日期"
									ref={cycleOrderDateRef}
									key={cycleOrder?.cycleOrderDate}
									defaultValue={cycleOrder?.cycleOrderDate ?? getNowTime()}
									minDate={getNowTime()}
									{...getErrorModuleStatus(validationErrors, "cycleOrderDate")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<SelectWithMenuItems
									label="TIME"
									ref={timeRef}
									key={cycleOrder?.cycleTimeType}
									defaultValue={cycleOrder?.cycleTimeType > 0 ? cycleOrder.cycleTimeType : ""}
									options={options?.cycleTimeTypeOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "cycleTimeType")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<SelectWithMenuItems
									label="TOOL"
									ref={toolRef}
									key={cycleOrder?.cycleToolType}
									defaultValue={cycleOrder?.cycleToolType > 0 ? cycleOrder.cycleToolType : ""}
									options={options?.cycleToolTypeOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "cycleToolType")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<SelectWithMenuItems
									label="所屬店舖"
									ref={clubRef}
									key={cycleOrder?.clubID}
									defaultValue={cycleOrder?.clubID ?? 0}
									options={options?.clubOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "clubID")}
									fullWidth
									required
									disabled
								/>
							</Grid>
							<Grid xs={4} item>
								<SelectWithMenuItems
									label="販售店舖"
									ref={saleClubRef}
									key={cycleOrder?.saleClubID}
									defaultValue={cycleOrder?.saleClubID ?? 0}
									options={options?.clubOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "saleClubID")}
									fullWidth
									required
									disabled
								/>
							</Grid>
							<Grid xs={4} item>
								<EmployeeSelect label="擔當" ref={employeeRef} options={options?.coachOptions ?? []} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
					<Box className={"text-right btn-group px-4 pb-3"}>
						<Button variant={"outlined"} onClick={() => focusModeRef.current.checkAndExitFocusMode(handleTemporaryCycleOrder)}>
							暫存資料
						</Button>
						<Button variant={"contained"} onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmit)}>
							確認送出
						</Button>
					</Box>
				</Card>
				{/* 暫存資料確認彈窗 */}
				<ConfirmTemporaryContractDialog open={openDialog?.isOpen ?? false} handleClose={handleDialogClose} handleClickYes={handleDialogYes} handleClickNo={handleDialogNo} />
			</FocusModeToggle>
		</Box>
	);
};

export default CyclePurchasePage;
